import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { BACKEND_URL } from '../config';
import { parseNumberInput, getNumberInputValue } from '../utils/numberInputHelpers';
import { DEFAULT_TAX_RATE, loadSystemFeatures } from '../utils/systemFeatures';
import ExportConfigModal from './ExportConfigModal';
import { buildCsv, DEFAULT_COLUMNS_BASIC } from '../utils/exportColumns';

const TIMEZONE_OPTIONS = [
  { value: 'America/Mexico_City', label: 'Mexico City (UTC-6)' },
  { value: 'America/Tijuana', label: 'Tijuana (UTC-8)' },
  { value: 'America/Cancun', label: 'Cancun (UTC-5)' },
  { value: 'America/Monterrey', label: 'Monterrey (UTC-6)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (UTC-8/7)' },
  { value: 'America/Denver', label: 'Mountain Time (UTC-7/6)' },
  { value: 'America/Chicago', label: 'Central Time (UTC-6/5)' },
  { value: 'America/New_York', label: 'Eastern Time (UTC-5/4)' }
];

function SystemSettings({ theme, user }) {
  const { t, language, translateInLanguage } = useLanguage();

  const [settings, setSettings] = useState({
    dailyLogoutHour: 5,
    dailyLogoutMinute: 0,
    dailyLogoutTimezone: 'America/Mexico_City',
    sessionTimeoutHours: 5,
    dailyLogoutEnabled: true,
    itemSortOrder: 'display',
    categorySortOrder: 'display',
    kitchenOutputMode: 'display',
    printDontMakeItems: false,
    tableAreasEnabled: false,
    orderLabelsEnabled: false,
    serviceChargeEnabled: false,
    splitPaymentsEnabled: false,
    customItemsEnabled: false,
    taxRate: DEFAULT_TAX_RATE
  });

  const [saveStatus, setSaveStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [kdsTooltipVisible, setKdsTooltipVisible] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupStatus, setBackupStatus] = useState('');
  const [csvLoading, setCsvLoading] = useState(null); // 'all' or 'ytd' or null
  const [exportModalPeriod, setExportModalPeriod] = useState(null); // 'all' | 'ytd' | null

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/system-settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(prevSettings => ({ ...prevSettings, ...data }));
      } else {
        console.error('Failed to load system settings');
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfiguration = async () => {
    try {
      setSaveStatus(t('saving') || 'Saving...');

      const response = await fetch(`${BACKEND_URL}/api/system-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        // Refresh the cache the receipt and ticket builders read, so a new tax
        // rate reaches them without a reload
        loadSystemFeatures();
        setSaveStatus(t('settingsSaved'));
        setTimeout(() => setSaveStatus(''), 3000);
      } else {
        const error = await response.json();
        setSaveStatus(t('error') + ': ' + (error.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
      setSaveStatus(t('error') + ': ' + error.message);
    }
  };

  const handleCancelConfiguration = () => {
    loadConfiguration();
    setSaveStatus('');
  };

  const handleBackup = async () => {
    setBackupLoading(true);
    setBackupStatus('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/backup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user?.user_id || user?.id })
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || t('backupFailed'));
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1] || 'backup.sql';
      a.click();
      window.URL.revokeObjectURL(url);
      setBackupStatus(t('backupCreated'));
    } catch (err) {
      console.error('Backup error:', err);
      setBackupStatus(`${t('backupFailed')}: ${err.message}`);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleExportSalesCSV = (period) => {
    setExportModalPeriod(period);
  };

  const runExportSalesCSV = async ({ language: exportLang, selectedColumns }) => {
    const period = exportModalPeriod;
    if (!period) return;
    setCsvLoading(period);
    try {
      const url = period === 'all'
        ? `${BACKEND_URL}/api/admin/export-statistics`
        : `${BACKEND_URL}/api/admin/export-statistics?period=year`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch sales data');
      const data = await response.json();

      const csvContent = buildCsv(data, selectedColumns, translateInLanguage, exportLang);

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const exportDate = new Date().toISOString().split('T')[0];
      const dates = data.map(r => r.order_created).filter(Boolean).map(d => new Date(d));
      const fromDate = dates.length > 0 ? new Date(Math.min(...dates)).toISOString().split('T')[0] : exportDate;
      const toDate = dates.length > 0 ? new Date(Math.max(...dates)).toISOString().split('T')[0] : exportDate;
      const label = period === 'all' ? 'all_time' : 'ytd';
      link.download = `sales_${label}_${fromDate}_to_${toDate}_exported_${exportDate}.csv`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setBackupStatus(`${t('export')}: ${data.length} ${t('records').toLowerCase()}`);
      setExportModalPeriod(null);
    } catch (err) {
      console.error('CSV export error:', err);
      setBackupStatus(`${t('error')}: ${err.message}`);
    } finally {
      setCsvLoading(null);
    }
  };

  if (loading) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: theme?.colors?.textSecondary || '#666'
      }}>
        {t('loading')}...
      </div>
    );
  }

  return (
    <div style={{
      padding: '1.5rem',
      background: theme?.colors?.background || '#f5f5f5'
    }}>
      <div style={{
        backgroundColor: theme?.colors?.surface || '#fff',
        borderRadius: theme?.borderRadius || 8,
        padding: '2rem',
        boxShadow: theme?.boxShadow || '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        {/* Daily Auto-Logout Settings */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{
            margin: '0 0 1.5rem 0',
            color: theme?.colors?.primary || '#1976d2',
            borderBottom: `2px solid ${theme?.colors?.primary || '#1976d2'}`,
            paddingBottom: '0.5rem'
          }}>
            {t('dailyAutoLogout')}
          </h3>

          {/* Enable/Disable Daily Logout */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: theme?.colors?.text || '#000'
            }}>
              <input
                type="checkbox"
                checked={settings.dailyLogoutEnabled}
                onChange={(e) => setSettings({ ...settings, dailyLogoutEnabled: e.target.checked })}
                style={{
                  marginRight: '0.5rem',
                  cursor: 'pointer',
                  width: '18px',
                  height: '18px'
                }}
              />
              <span style={{ fontWeight: 'bold' }}>{t('dailyAutoLogoutEnabled')}</span>
            </label>
            <p style={{
              margin: '0.5rem 0 0 1.8rem',
              fontSize: '0.85rem',
              color: theme?.colors?.textSecondary || '#666'
            }}>
              {t('dailyAutoLogoutInfo')}
            </p>
          </div>

          {/* Logout Time Settings */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1rem',
            marginBottom: '1rem',
            opacity: settings.dailyLogoutEnabled ? 1 : 0.5,
            pointerEvents: settings.dailyLogoutEnabled ? 'auto' : 'none'
          }}>
            {/* Logout Hour */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 'bold',
                color: theme?.colors?.text || '#000'
              }}>
                {t('dailyLogoutHour')} (0-23):
              </label>
              <input
                type="number"
                value={getNumberInputValue(settings.dailyLogoutHour)}
                onChange={(e) => setSettings({
                  ...settings,
                  dailyLogoutHour: parseNumberInput(e.target.value, { min: 0, max: 23 })
                })}
                onWheel={(e) => e.target.blur()}
                placeholder="5"
                disabled={!settings.dailyLogoutEnabled}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                  borderRadius: theme?.borderRadius || 4,
                  fontSize: '1rem',
                  backgroundColor: theme?.colors?.surface || '#fff',
                  color: theme?.colors?.text || '#000'
                }}
              />
            </div>

            {/* Logout Minute */}
            <div>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 'bold',
                color: theme?.colors?.text || '#000'
              }}>
                {t('dailyLogoutMinute')} (0-59):
              </label>
              <input
                type="number"
                value={getNumberInputValue(settings.dailyLogoutMinute)}
                onChange={(e) => setSettings({
                  ...settings,
                  dailyLogoutMinute: parseNumberInput(e.target.value, { min: 0, max: 59 })
                })}
                onWheel={(e) => e.target.blur()}
                placeholder="0"
                disabled={!settings.dailyLogoutEnabled}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                  borderRadius: theme?.borderRadius || 4,
                  fontSize: '1rem',
                  backgroundColor: theme?.colors?.surface || '#fff',
                  color: theme?.colors?.text || '#000'
                }}
              />
            </div>

            {/* Timezone */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: 'bold',
                color: theme?.colors?.text || '#000'
              }}>
                {t('dailyLogoutTimezone')}:
              </label>
              <select
                value={settings.dailyLogoutTimezone}
                onChange={(e) => setSettings({ ...settings, dailyLogoutTimezone: e.target.value })}
                disabled={!settings.dailyLogoutEnabled}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                  borderRadius: theme?.borderRadius || 4,
                  fontSize: '1rem',
                  backgroundColor: theme?.colors?.surface || '#fff',
                  color: theme?.colors?.text || '#000',
                  cursor: 'pointer'
                }}
              >
                {TIMEZONE_OPTIONS.map(tz => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
              <p style={{
                margin: '0.5rem 0 0 0',
                fontSize: '0.85rem',
                color: theme?.colors?.textSecondary || '#666'
              }}>
                {t('timezoneInfo')}
              </p>
            </div>
          </div>
        </div>

        {/* Session Timeout Settings */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{
            margin: '0 0 1.5rem 0',
            color: theme?.colors?.primary || '#1976d2',
            borderBottom: `2px solid ${theme?.colors?.primary || '#1976d2'}`,
            paddingBottom: '0.5rem'
          }}>
            {t('sessionSettings')}
          </h3>

          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 'bold',
              color: theme?.colors?.text || '#000'
            }}>
              {t('sessionTimeoutHours')}:
            </label>
            <input
              type="number"
              value={getNumberInputValue(settings.sessionTimeoutHours)}
              onChange={(e) => setSettings({
                ...settings,
                sessionTimeoutHours: parseNumberInput(e.target.value, { min: 1, max: 24 })
              })}
              onWheel={(e) => e.target.blur()}
              placeholder="5"
              style={{
                width: '200px',
                padding: '0.5rem',
                border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                borderRadius: theme?.borderRadius || 4,
                fontSize: '1rem',
                backgroundColor: theme?.colors?.surface || '#fff',
                color: theme?.colors?.text || '#000'
              }}
            />
            <p style={{
              margin: '0.5rem 0 0 0',
              fontSize: '0.85rem',
              color: theme?.colors?.textSecondary || '#666'
            }}>
              {t('inactivityTimeout') || 'Number of hours of inactivity before automatic logout'}
            </p>
          </div>
        </div>

        {/* Product Display Settings */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{
            margin: '0 0 1.5rem 0',
            color: theme?.colors?.primary || '#1976d2',
            borderBottom: `2px solid ${theme?.colors?.primary || '#1976d2'}`,
            paddingBottom: '0.5rem'
          }}>
            {t('productDisplaySettings')}
          </h3>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.75rem',
              fontWeight: 'bold',
              color: theme?.colors?.text || '#000'
            }}>
              {t('itemSortOrder')}:
            </label>

            {/* Radio button options */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                color: theme?.colors?.text || '#000'
              }}>
                <input
                  type="radio"
                  name="itemSortOrder"
                  value="display"
                  checked={settings.itemSortOrder === 'display'}
                  onChange={(e) => setSettings({ ...settings, itemSortOrder: e.target.value })}
                  style={{
                    marginRight: '0.5rem',
                    cursor: 'pointer',
                    width: '18px',
                    height: '18px'
                  }}
                />
                <div>
                  <span style={{ fontWeight: 'bold' }}>{t('displayOrder')}</span>
                  <p style={{
                    margin: '0.25rem 0 0 1.8rem',
                    fontSize: '0.85rem',
                    color: theme?.colors?.textSecondary || '#666'
                  }}>
                    {t('sortItemsByDisplayOrder')}
                  </p>
                </div>
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                color: theme?.colors?.text || '#000'
              }}>
                <input
                  type="radio"
                  name="itemSortOrder"
                  value="alphabetical"
                  checked={settings.itemSortOrder === 'alphabetical'}
                  onChange={(e) => setSettings({ ...settings, itemSortOrder: e.target.value })}
                  style={{
                    marginRight: '0.5rem',
                    cursor: 'pointer',
                    width: '18px',
                    height: '18px'
                  }}
                />
                <div>
                  <span style={{ fontWeight: 'bold' }}>{t('alphabeticalOrder')}</span>
                  <p style={{
                    margin: '0.25rem 0 0 1.8rem',
                    fontSize: '0.85rem',
                    color: theme?.colors?.textSecondary || '#666'
                  }}>
                    {t('sortItemsAlphabetically')}
                  </p>
                </div>
              </label>
            </div>

            {/* Info message */}
            <p style={{
              margin: '1rem 0 0 0',
              padding: '0.75rem',
              fontSize: '0.85rem',
              color: theme?.colors?.textSecondary || '#666',
              backgroundColor: theme?.colors?.background || '#f5f5f5',
              borderRadius: theme?.borderRadius || 4,
              borderLeft: `3px solid ${theme?.colors?.primary || '#1976d2'}`
            }}>
              ℹ️ {t('sortOrderChangeInfo')}
            </p>
          </div>

          {/* Category Sort Order */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.75rem',
              fontWeight: 'bold',
              color: theme?.colors?.text || '#000'
            }}>
              {t('categorySortOrder')}:
            </label>

            {/* Radio button options */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                color: theme?.colors?.text || '#000'
              }}>
                <input
                  type="radio"
                  name="categorySortOrder"
                  value="display"
                  checked={settings.categorySortOrder === 'display'}
                  onChange={(e) => setSettings({ ...settings, categorySortOrder: e.target.value })}
                  style={{
                    marginRight: '0.5rem',
                    cursor: 'pointer',
                    width: '18px',
                    height: '18px'
                  }}
                />
                <div>
                  <span style={{ fontWeight: 'bold' }}>{t('displayOrder')}</span>
                  <p style={{
                    margin: '0.25rem 0 0 1.8rem',
                    fontSize: '0.85rem',
                    color: theme?.colors?.textSecondary || '#666'
                  }}>
                    {t('sortCategoriesByDisplayOrder')}
                  </p>
                </div>
              </label>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                color: theme?.colors?.text || '#000'
              }}>
                <input
                  type="radio"
                  name="categorySortOrder"
                  value="alphabetical"
                  checked={settings.categorySortOrder === 'alphabetical'}
                  onChange={(e) => setSettings({ ...settings, categorySortOrder: e.target.value })}
                  style={{
                    marginRight: '0.5rem',
                    cursor: 'pointer',
                    width: '18px',
                    height: '18px'
                  }}
                />
                <div>
                  <span style={{ fontWeight: 'bold' }}>{t('alphabeticalOrder')}</span>
                  <p style={{
                    margin: '0.25rem 0 0 1.8rem',
                    fontSize: '0.85rem',
                    color: theme?.colors?.textSecondary || '#666'
                  }}>
                    {t('sortCategoriesAlphabetically')}
                  </p>
                </div>
              </label>
            </div>

            {/* Info message */}
            <p style={{
              margin: '1rem 0 0 0',
              padding: '0.75rem',
              fontSize: '0.85rem',
              color: theme?.colors?.textSecondary || '#666',
              backgroundColor: theme?.colors?.background || '#f5f5f5',
              borderRadius: theme?.borderRadius || 4,
              borderLeft: `3px solid ${theme?.colors?.primary || '#1976d2'}`
            }}>
              ℹ️ {t('categorySortOrderChangeInfo')}
            </p>
          </div>
        </div>

        {/* Kitchen Display System Settings */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{
            margin: '0 0 1.5rem 0',
            color: theme?.colors?.primary || '#1976d2',
            borderBottom: `2px solid ${theme?.colors?.primary || '#1976d2'}`,
            paddingBottom: '0.5rem'
          }}>
            {t('kitchenDisplaySettings')}
          </h3>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: theme?.colors?.text || '#000'
            }}>
              <span style={{ fontWeight: 'bold' }}>{t('kitchenOutputMode') || 'Kitchen Output Mode'}:</span>
              <select
                value={settings.kitchenOutputMode}
                onChange={(e) => setSettings({ ...settings, kitchenOutputMode: e.target.value })}
                style={{
                  padding: '0.4rem 0.6rem',
                  borderRadius: theme?.borderRadius || 4,
                  border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                  backgroundColor: theme?.colors?.surface || '#fff',
                  color: theme?.colors?.text || '#000',
                  fontSize: '0.9rem',
                  cursor: 'pointer'
                }}
              >
                <option value="display">{t('displayOnly') || 'Display Only'}</option>
                <option value="tickets">{t('ticketsOnly') || 'Tickets Only'}</option>
                <option value="both">{t('displayAndTickets') || 'Display + Tickets'}</option>
                <option value="none">{t('noKitchenOutput') || 'None'}</option>
              </select>
              <span
                style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
                onMouseEnter={() => setKdsTooltipVisible(true)}
                onMouseLeave={() => setKdsTooltipVisible(false)}
              >
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  backgroundColor: theme?.colors?.primary || '#1976d2',
                  color: theme?.colors?.textLight || '#fff',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  cursor: 'help',
                  userSelect: 'none',
                  flexShrink: 0
                }}>i</span>
                {kdsTooltipVisible && (
                  <div style={{
                    position: 'absolute',
                    bottom: '130%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: theme?.colors?.text || '#333',
                    color: theme?.colors?.surface || '#fff',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    maxWidth: '320px',
                    width: 'max-content',
                    zIndex: 1000,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                    pointerEvents: 'none',
                    lineHeight: '1.4'
                  }}>
                    {t('kitchenOutputModeInfo') || 'Display: KDS screen only. Tickets: print item tickets only. Both: KDS screen + print tickets. None: disabled.'}
                  </div>
                )}
              </span>
            </label>
            <p style={{
              margin: '0.5rem 0 0 0',
              fontSize: '0.85rem',
              color: theme?.colors?.textSecondary || '#666'
            }}>
              {t('kitchenOutputModeInfo') || 'Display: KDS screen only. Tickets: print item tickets only. Both: KDS screen + print tickets. None: disabled.'}
            </p>
          </div>

          {(settings.kitchenOutputMode === 'tickets' || settings.kitchenOutputMode === 'both') && (
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                color: theme?.colors?.text || '#000'
              }}>
                <input
                  type="checkbox"
                  checked={settings.printDontMakeItems}
                  onChange={(e) => setSettings({ ...settings, printDontMakeItems: e.target.checked })}
                  style={{
                    marginRight: '0.5rem',
                    cursor: 'pointer',
                    width: '18px',
                    height: '18px'
                  }}
                />
                <span style={{ fontWeight: 'bold' }}>{t('printDontMakeItems') || 'Print "Don\'t Make" items on tickets'}</span>
              </label>
              <p style={{
                margin: '0.5rem 0 0 1.8rem',
                fontSize: '0.85rem',
                color: theme?.colors?.textSecondary || '#666'
              }}>
                {t('printDontMakeItemsInfo') || 'When enabled, items marked as "Don\'t Make" will appear on printed tickets (marked as NO PREPARAR).'}
              </p>
            </div>
          )}
        </div>

        {/* Feature Settings */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{
            margin: '0 0 1.5rem 0',
            color: theme?.colors?.primary || '#1976d2',
            borderBottom: `2px solid ${theme?.colors?.primary || '#1976d2'}`,
            paddingBottom: '0.5rem'
          }}>
            {t('featureSettings')}
          </h3>

          {/* Table Areas */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: theme?.colors?.text || '#000'
            }}>
              <input
                type="checkbox"
                checked={settings.tableAreasEnabled === true}
                onChange={(e) => setSettings({ ...settings, tableAreasEnabled: e.target.checked })}
                style={{
                  marginRight: '0.5rem',
                  cursor: 'pointer',
                  width: '18px',
                  height: '18px'
                }}
              />
              <span style={{ fontWeight: 'bold' }}>{t('tableAreasEnabled')}</span>
            </label>
            <p style={{
              margin: '0.5rem 0 0 1.8rem',
              fontSize: '0.85rem',
              color: theme?.colors?.textSecondary || '#666'
            }}>
              {t('tableAreasEnabledInfo')}
            </p>
          </div>

          {/* Order Labels */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: theme?.colors?.text || '#000'
            }}>
              <input
                type="checkbox"
                checked={settings.orderLabelsEnabled === true}
                onChange={(e) => setSettings({ ...settings, orderLabelsEnabled: e.target.checked })}
                style={{
                  marginRight: '0.5rem',
                  cursor: 'pointer',
                  width: '18px',
                  height: '18px'
                }}
              />
              <span style={{ fontWeight: 'bold' }}>{t('orderLabelsEnabled')}</span>
            </label>
            <p style={{
              margin: '0.5rem 0 0 1.8rem',
              fontSize: '0.85rem',
              color: theme?.colors?.textSecondary || '#666'
            }}>
              {t('orderLabelsEnabledInfo')}
            </p>
          </div>

          {/* Service Charge */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: theme?.colors?.text || '#000'
            }}>
              <input
                type="checkbox"
                checked={settings.serviceChargeEnabled === true}
                onChange={(e) => setSettings({ ...settings, serviceChargeEnabled: e.target.checked })}
                style={{
                  marginRight: '0.5rem',
                  cursor: 'pointer',
                  width: '18px',
                  height: '18px'
                }}
              />
              <span style={{ fontWeight: 'bold' }}>{t('serviceChargeEnabled')}</span>
            </label>
            <p style={{
              margin: '0.5rem 0 0 1.8rem',
              fontSize: '0.85rem',
              color: theme?.colors?.textSecondary || '#666'
            }}>
              {t('serviceChargeEnabledInfo')}
            </p>
          </div>

          {/* Split Payments */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: theme?.colors?.text || '#000'
            }}>
              <input
                type="checkbox"
                checked={settings.splitPaymentsEnabled === true}
                onChange={(e) => setSettings({ ...settings, splitPaymentsEnabled: e.target.checked })}
                style={{
                  marginRight: '0.5rem',
                  cursor: 'pointer',
                  width: '18px',
                  height: '18px'
                }}
              />
              <span style={{ fontWeight: 'bold' }}>{t('splitPaymentsEnabled')}</span>
            </label>
            <p style={{
              margin: '0.5rem 0 0 1.8rem',
              fontSize: '0.85rem',
              color: theme?.colors?.textSecondary || '#666'
            }}>
              {t('splitPaymentsEnabledInfo')}
            </p>
          </div>

          {/* Custom Items */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: theme?.colors?.text || '#000'
            }}>
              <input
                type="checkbox"
                checked={settings.customItemsEnabled === true}
                onChange={(e) => setSettings({ ...settings, customItemsEnabled: e.target.checked })}
                style={{
                  marginRight: '0.5rem',
                  cursor: 'pointer',
                  width: '18px',
                  height: '18px'
                }}
              />
              <span style={{ fontWeight: 'bold' }}>{t('customItemsEnabled')}</span>
            </label>
            <p style={{
              margin: '0.5rem 0 0 1.8rem',
              fontSize: '0.85rem',
              color: theme?.colors?.textSecondary || '#666'
            }}>
              {t('customItemsEnabledInfo')}
            </p>
          </div>

          {/* Tax Rate */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 'bold',
              color: theme?.colors?.text || '#000'
            }}>
              {t('taxRate')} (%):
            </label>
            <input
              type="number"
              step="0.01"
              value={getNumberInputValue(settings.taxRate)}
              onChange={(e) => setSettings({
                ...settings,
                taxRate: parseNumberInput(e.target.value, { allowDecimals: true, min: 0, max: 100 })
              })}
              onWheel={(e) => e.target.blur()}
              placeholder={String(DEFAULT_TAX_RATE)}
              style={{
                width: '200px',
                padding: '0.5rem',
                border: `1px solid ${theme?.colors?.border || '#ccc'}`,
                borderRadius: theme?.borderRadius || 4,
                fontSize: '1rem',
                backgroundColor: theme?.colors?.surface || '#fff',
                color: theme?.colors?.text || '#000'
              }}
            />
            <p style={{
              margin: '0.5rem 0 0 0',
              fontSize: '0.85rem',
              color: theme?.colors?.textSecondary || '#666'
            }}>
              {t('taxRateInfo')}
            </p>
          </div>
        </div>

        {/* Database Backup - Owner only */}
        {user?.user_role === 4 && (
          <div style={{
            marginTop: '2rem',
            paddingTop: '1.5rem',
            borderTop: `1px solid ${theme?.colors?.border || '#ddd'}`
          }}>
            <h3 style={{
              margin: '0 0 0.5rem 0',
              color: theme?.colors?.text || '#000',
              fontSize: '1.1rem',
              fontWeight: 600
            }}>
              {t('BackupDatabase')}
            </h3>
            <p style={{
              margin: '0 0 1rem 0',
              fontSize: '0.85rem',
              color: theme?.colors?.textSecondary || '#666'
            }}>
              {t('backupDescription')}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button
                onClick={handleBackup}
                disabled={backupLoading}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: backupLoading ? (theme?.colors?.disabled || '#ccc') : (theme?.colors?.primary || '#1976d2'),
                  color: theme?.colors?.textLight || '#fff',
                  border: 'none',
                  borderRadius: theme?.borderRadius || 4,
                  cursor: backupLoading ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 'bold',
                  opacity: backupLoading ? 0.6 : 1
                }}
              >
                {backupLoading ? t('backupInProgress') : `💾 ${t('downloadBackup')}`}
              </button>
              <button
                onClick={() => handleExportSalesCSV('all')}
                disabled={csvLoading !== null}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: csvLoading === 'all' ? (theme?.colors?.disabled || '#ccc') : (theme?.colors?.success || '#4caf50'),
                  color: theme?.colors?.textLight || '#fff',
                  border: 'none',
                  borderRadius: theme?.borderRadius || 4,
                  cursor: csvLoading !== null ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 'bold',
                  opacity: csvLoading !== null ? 0.6 : 1
                }}
              >
                {csvLoading === 'all' ? '...' : `📊 ${t('salesAllTime')}`}
              </button>
              <button
                onClick={() => handleExportSalesCSV('ytd')}
                disabled={csvLoading !== null}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: csvLoading === 'ytd' ? (theme?.colors?.disabled || '#ccc') : (theme?.colors?.secondary || '#d4af37'),
                  color: theme?.colors?.textLight || '#fff',
                  border: 'none',
                  borderRadius: theme?.borderRadius || 4,
                  cursor: csvLoading !== null ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 'bold',
                  opacity: csvLoading !== null ? 0.6 : 1
                }}
              >
                {csvLoading === 'ytd' ? '...' : `📈 ${t('salesYTD')}`}
              </button>
            </div>
            {backupStatus && (
              <div style={{
                marginTop: '0.75rem',
                fontSize: '0.9rem',
                color: backupStatus.includes(t('error')) || backupStatus.includes(t('backupFailed'))
                  ? (theme?.colors?.error || '#f44336')
                  : (theme?.colors?.success || '#4caf50')
              }}>
                {backupStatus}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end',
          borderTop: `1px solid ${theme?.colors?.border || '#ddd'}`,
          paddingTop: '1.5rem'
        }}>
          <button
            onClick={handleCancelConfiguration}
            style={{
              padding: '0.75rem 2rem',
              backgroundColor: theme?.colors?.secondary || '#666',
              color: theme?.colors?.textLight || '#fff',
              border: 'none',
              borderRadius: theme?.borderRadius || 4,
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold'
            }}
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSaveConfiguration}
            style={{
              padding: '0.75rem 2rem',
              backgroundColor: theme?.colors?.primary || '#1976d2',
              color: theme?.colors?.textLight || '#fff',
              border: 'none',
              borderRadius: theme?.borderRadius || 4,
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: 'bold'
            }}
          >
            {t('save')}
          </button>
        </div>

        {/* Save Status Message */}
        {saveStatus && (
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem',
            backgroundColor: saveStatus.includes(t('error'))
              ? (theme?.colors?.error || '#f44336')
              : (theme?.colors?.success || '#4caf50'),
            color: theme?.colors?.textLight || '#fff',
            borderRadius: theme?.borderRadius || 4,
            textAlign: 'center',
            fontWeight: 'bold'
          }}>
            {saveStatus}
          </div>
        )}
      </div>

      <ExportConfigModal
        isOpen={exportModalPeriod !== null}
        onClose={() => setExportModalPeriod(null)}
        onConfirm={runExportSalesCSV}
        theme={theme}
        callerKey={`systemSettings:${exportModalPeriod || ''}`}
        defaultLanguage={language}
        defaultSelectedColumns={DEFAULT_COLUMNS_BASIC}
        exporting={csvLoading !== null}
      />
    </div>
  );
}

export default SystemSettings;
